import { Subhead } from "../components/SettingsLayout";
import { ChevronDownIcon, ChevronRightIcon, ShieldLockIcon } from "@primer/octicons-react";
import { rp } from "../api/client";
import { Fragment, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AUDIT_PAGE, useAudit, useOrgAudit, type AuditFilter } from "../api/hooks";
import type { AuditEntry } from "../api/types";
import { AUDIT_CATEGORIES, describeAudit } from "../components/AuditEvent";
import { Avatar, CopyButton, Empty, Pre, Query, TimeAgo, Who } from "../components/common";
import { toneText } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Chip, ChipGroup, DismissChip } from "../components/ui/toggle-group";
import { Tooltip } from "../components/ui/tooltip";

export function AuditTable({ entries, repo, onActor }: { entries: AuditEntry[]; repo?: string; onActor?: (actor: string) => void }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <Table>
      <TableHeader>
        <tr>
          <TableHead>When</TableHead>
          <TableHead>Actor</TableHead>
          <TableHead>Event</TableHead>
          <TableHead aria-label="Details" />
        </tr>
      </TableHeader>
      <TableBody>
        {entries.map((a) => {
          const ev = describeAudit(a, repo);
          const changeId: string | undefined = a.detail?.change_id ?? undefined;
          const expanded = open === a.seq;
          return (
            <Fragment key={a.seq}>
              <TableRow>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  <TimeAgo ms={a.ts} />
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {onActor ? (
                    <Tooltip content={`Only show events by ${a.actor}`}>
                      <Button variant="ghost" size="sm" onClick={() => onActor(a.actor)}>
                        <Avatar name={a.actor} size={18} /> {a.actor}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Who name={a.actor} size={18} />
                  )}
                </TableCell>
                <TableCell className="break-words">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <ev.Icon size={16} className={toneText[ev.tone]} />
                    {ev.text}
                    {repo && changeId && (
                      <Tooltip content="Open change">
                        <Link className="font-mono text-xs" to={`${rp(repo)}/changes/${changeId}`}>
                          #{changeId.slice(-8)}
                        </Link>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
                <TableCell className="w-px whitespace-nowrap">
                  <Tooltip content={expanded ? "Hide raw entry" : "Show raw entry"}>
                    <Button variant="ghost" size="sm" onClick={() => setOpen(expanded ? null : a.seq)} aria-expanded={expanded} aria-label="Raw entry">
                      {expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                    </Button>
                  </Tooltip>
                </TableCell>
              </TableRow>
              {expanded && (
                <tr>
                  <TableCell colSpan={4} className="bg-muted">
                    <div className="mb-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>
                        #{a.seq} · <span className="font-mono">{a.action}</span> · {new Date(a.ts).toLocaleString()}
                      </span>
                      <CopyButton value={JSON.stringify(a, null, 2)} title="Copy entry as JSON" />
                    </div>
                    <Pre className="mt-0 max-h-90 bg-background">{JSON.stringify(a.detail, null, 2)}</Pre>
                  </TableCell>
                </tr>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}

/** The repo's Activity tab: the append-only audit trail of every agent and admin action. */
export function RepoActivity() {
  return (
    <>
      <Subhead title="Activity">Every mutation and policy decision on this repository, append-only.</Subhead>
      <Card className="mt-0">
        <AuditLog repo={useParams().repo} />
      </Card>
    </>
  );
}

/** Filterable, paged audit trail: one repository, or one organization's repositories and settings. */
export function AuditLog({ repo, org }: { repo?: string; org?: string }) {
  const [params, setParams] = useSearchParams();
  const [cursor, setCursor] = useState<number | undefined>();
  const category = params.get("category") ?? "";
  const actor = params.get("actor") ?? "";
  const cats = AUDIT_CATEGORIES.filter((c) => !!org || !c.org);
  const f: AuditFilter = { cursor, actor, action: cats.find((c) => c.id === category)?.prefixes.join(",") };
  const repoQ = useAudit(repo ?? "", f, !!repo);
  const orgQ = useOrgAudit(org ?? "", f, !!org);
  const q = repo ? repoQ : orgQ;

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
    setCursor(undefined);
  };

  return (
    <>
      <CardContent className="flex flex-wrap items-center gap-1.5 border-b">
        <ChipGroup aria-label="Category" value={category || "all"} onValueChange={(v) => set("category", v === "all" ? "" : v)}>
          <Chip value="all">All</Chip>
          {cats.map((c) => (
            <Chip key={c.id} value={c.id}>
              {c.label}
            </Chip>
          ))}
        </ChipGroup>
        {actor && (
          <Tooltip content="Clear actor filter">
            <DismissChip className="ml-auto" onDismiss={() => set("actor", "")} label={`Clear actor filter: ${actor}`}>
              <Avatar name={actor} size={14} /> {actor}
            </DismissChip>
          </Tooltip>
        )}
      </CardContent>
      <Query q={q}>
        {(entries) =>
          entries.length ? (
            <>
              <AuditTable entries={entries} repo={repo} onActor={(a) => set("actor", a === actor ? "" : a)} />
              {(cursor || entries.length >= AUDIT_PAGE) && (
                <CardContent className="flex items-center gap-2 border-t">
                  {cursor && (
                    <Button size="sm" onClick={() => setCursor(undefined)}>
                      Newest
                    </Button>
                  )}
                  {entries.length >= AUDIT_PAGE && (
                    <Button size="sm" className="ml-auto" onClick={() => setCursor(entries[entries.length - 1].seq)}>
                      Older
                    </Button>
                  )}
                </CardContent>
              )}
            </>
          ) : (
            <Empty icon={ShieldLockIcon}>{category || actor ? "No audit entries match these filters." : "No audit entries."}</Empty>
          )
        }
      </Query>
    </>
  );
}
